import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Euro, Heart, Package, Eye } from "lucide-react";
import { useProductViews, formatViews } from "../../hooks/useProductViews";
import { isSoldOut, SoldOutStamp, SOLD_OUT_IMG } from "./SoldOutStamp";
import { ReviewSection } from "./ReviewSection";
import { PreorderCountdown } from "./PreorderCountdown";
import { NoBgImage } from "./NoBgImage";
import { formatPrice, formatPriceWithIva, getDiscountedPrice } from "./pricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// New arrival = article created in the last 7 days (matches backend is_new logic)
export const isNewArrival = (a) => {
  if (!a?.created_at) return false;
  const d = new Date(a.created_at);
  return !isNaN(d.getTime()) && Date.now() - d.getTime() < 7 * 24 * 3600 * 1000;
};

const AvailabilityBadge = ({ article }) => {
  if (article.availability_type === "preorder") {
    return <Badge className="bg-orange-100 text-orange-800 border-orange-200">🚀 Prevendita</Badge>;
  }
  if (article.availability_type === "reservation") {
    return <Badge className="bg-blue-100 text-blue-800 border-blue-200">📋 Prenotazione</Badge>;
  }
  if (article.quantity_stock > 0) {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200">
        Disponibile ({article.quantity_stock})
      </Badge>
    );
  }
  return <Badge variant="destructive">Esaurito</Badge>;
};

export const promoTimeLeft = (expiry) => {
  if (!expiry) return null;
  const ms = new Date(expiry).getTime() - Date.now();
  if (isNaN(ms) || ms <= 0) return null;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return { label: `${d}g ${h}h`, urgent: d < 1 };
  if (h > 0) return { label: `${h}h ${m}m`, urgent: true };
  return { label: `${m} min`, urgent: true };
};

const PriceBlock = ({ article, discount }) => {
  if (discount) {
    const discountedPrice = getDiscountedPrice(article.price, article.iva_percentage, discount);
    return (
      <div className="flex flex-col">
        <span className="text-sm text-gray-400 line-through">
          {formatPriceWithIva(article.price, article.iva_percentage)}
        </span>
        <span className="font-bold text-lg text-red-600" data-testid="discounted-price">
          {formatPrice(discountedPrice)}
        </span>
      </div>
    );
  }
  return (
    <span className="font-bold text-lg text-pink-400">
      {formatPriceWithIva(article.price, article.iva_percentage)}
    </span>
  );
};

const AnalyticsBadges = ({ badges }) => {
  if (!badges) return null;
  return (
    <div className="flex flex-col gap-1">
      {badges.is_new && (
        <Badge className="bg-blue-500 text-white text-xs whitespace-nowrap">🆕 Nuovo</Badge>
      )}
      {badges.is_trending && (
        <Badge className="bg-orange-500 text-white text-xs whitespace-nowrap">🔥 Trending</Badge>
      )}
      {badges.is_popular && (
        <Badge className="bg-purple-500 text-white text-xs whitespace-nowrap">⭐ Popolare</Badge>
      )}
    </div>
  );
};

/**
 * Product card rendered inside the catalog grid. Encapsulates image+lightbox
 * trigger, badges, pricing, availability, add-to-cart / wishlist buttons,
 * and the review section.
 */
export const ProductCard = ({
  article,
  discount,
  badges,
  wishlistCount,
  wishlistIds,
  isFocused,
  onZoomImage,
  onAddToCart,
  onToggleWishlist,
  // Review section pass-through
  reviews,
  showReviewForm,
  reviewForm,
  setReviewForm,
  onToggleReviewForm,
  onSubmitReview,
}) => {
  const viewsMap = useProductViews();
  const views = viewsMap[article.id] || 0;
  const imageUrl = article.images_url?.[0] || (article.photo_url ? `${BACKEND_URL}${article.photo_url}` : null);
  const isWishlisted = wishlistIds.includes(article.id);
  const isAvailable = article.quantity_stock > 0;
  // Prefer SEO-friendly slug over UUID for the canonical product URL.
  // Falls back to id for any legacy article that hasn't been backfilled yet.
  const productUrl = `/prodotto/${article.slug || article.id}`;

  return (
    <Card
      id={`product-card-${article.id}`}
      className={`bg-[#12121a] border-gray-800 hover:border-pink-500/40 hover:shadow-[0_0_24px_rgba(236,72,153,0.12)] transition-all duration-300 ${
        isFocused ? "ring-2 ring-pink-500 ring-offset-2 ring-offset-[#0a0a0f]" : ""
      }`}
    >
      {imageUrl && (
        <Link
          to={productUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="relative aspect-[3/4] w-full overflow-hidden rounded-t-lg bg-[#0d0d14] group block"
          aria-label={`Vedi dettagli: ${article.title}`}
          data-testid={`product-image-link-${article.id}`}
        >
          <NoBgImage
            article={article}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isSoldOut(article) ? SOLD_OUT_IMG : ""}`}
          />
          {isSoldOut(article) && <SoldOutStamp articleId={article.id} />}
          <span className="absolute bottom-2 left-2 bg-pink-600 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            👁 Vedi dettagli ↗
          </span>
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1.5">
            {isNewArrival(article) && (
              <span
                className="bg-pink-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg"
                data-testid={`new-arrival-badge-${article.id}`}
              >
                🆕 NUOVO ARRIVO
              </span>
            )}
            {article.availability_type === "preorder" && (
              <span
                className="bg-amber-400 text-gray-900 text-xs font-extrabold px-2.5 py-1 rounded-full shadow-lg"
                data-testid={`preorder-badge-${article.id}`}
              >
                🔜 PREVENDITA{article.preorder_date ? ` · dal ${article.preorder_date.slice(8, 10)}/${article.preorder_date.slice(5, 7)}` : ""}
              </span>
            )}
          </div>
          {article.availability_type !== "preorder" && (article.quantity_stock ?? 0) > 0 && article.quantity_stock <= 2 && (
            <span
              className="absolute bottom-2 right-2 bg-orange-500 text-white text-xs font-extrabold px-2.5 py-1 rounded-full shadow-lg animate-pulse"
              data-testid={`last-pieces-badge-${article.id}`}
            >
              🔥 Ultimi pezzi!
            </span>
          )}
          {views > 0 && (
            <span
              className={`absolute ${article.availability_type !== "preorder" && (article.quantity_stock ?? 0) > 0 && article.quantity_stock <= 2 ? "bottom-10" : "bottom-2"} right-2 inline-flex items-center gap-1 bg-black/70 backdrop-blur text-gray-200 text-[11px] font-semibold px-2 py-0.5 rounded-full`}
              title={`${views} visualizzazioni`}
              data-testid={`product-views-${article.id}`}
            >
              <Eye className="w-3 h-3" /> {formatViews(views)}
            </span>
          )}
          {discount && (() => {
            const full = article.price * (1 + parseFloat(article.iva_percentage || 0) / 100);
            const discounted = getDiscountedPrice(article.price, article.iva_percentage, discount);
            const pct = full > 0 ? Math.round(((full - discounted) / full) * 100) : 0;
            if (pct <= 0) return null;
            const left = promoTimeLeft(discount.expiry_date);
            return (
              <span
                className="absolute top-2 right-2 flex flex-col items-center bg-red-600 text-white rounded-lg px-2.5 py-1.5 shadow-lg rotate-2"
                data-testid={`discount-percent-badge-${article.id}`}
              >
                <span className="text-base font-extrabold leading-none">-{pct}%</span>
                <span className="text-[9px] font-bold uppercase tracking-wide leading-tight mt-0.5">
                  risparmi €{(full - discounted).toFixed(2)}
                </span>
                {left && (
                  <span
                    className={`text-[9px] font-extrabold leading-tight mt-0.5 bg-black/30 rounded-full px-1.5 py-0.5 ${left.urgent ? "animate-pulse text-yellow-300" : "text-white"}`}
                    data-testid={`promo-countdown-${article.id}`}
                  >
                    ⏰ scade tra {left.label}
                  </span>
                )}
              </span>
            );
          })()}
        </Link>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <Link
            to={productUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 hover:text-pink-400 transition-colors"
            data-testid={`product-title-link-${article.id}`}
          >
            <CardTitle className="text-lg font-semibold text-white line-clamp-2">
              {article.title}
            </CardTitle>
          </Link>
          <AnalyticsBadges badges={badges} />
        </div>
        {article.category && (
          <Badge variant="secondary" className="w-fit bg-[#1c1c26] text-gray-300 border border-gray-700 hover:bg-[#22222e]">
            {article.category}
          </Badge>
        )}
        {badges?.trending_text && (
          <p className="text-xs text-orange-600 font-medium mt-1">👀 {badges.trending_text}</p>
        )}
        {wishlistCount >= 3 && (
          <p
            className="text-xs text-pink-600 font-medium mt-1 flex items-center gap-1"
            data-testid={`wishlist-social-${article.id}`}
          >
            <Heart className="w-3 h-3 fill-pink-500 text-pink-500" />
            {wishlistCount} {wishlistCount === 1 ? "persona" : "persone"}{" "}
            {wishlistCount === 1 ? "l'ha" : "l'hanno"} desiderato
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        <p className="text-gray-400 text-sm line-clamp-3">{article.description}</p>
        {article.description && article.description.length > 120 && (
          <Link
            to={productUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-pink-400 hover:underline font-medium inline-block"
            data-testid={`product-read-more-${article.id}`}
          >
            Leggi descrizione completa →
          </Link>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Euro className="w-4 h-4 text-pink-400" />
              <PriceBlock article={article} discount={discount} />
            </div>
            <div className="flex flex-col items-end gap-1">
              {discount && (
                <Badge className="bg-red-500 text-white text-xs" data-testid="discount-badge">
                  -
                  {discount.discount_type === "percentage"
                    ? `${discount.discount_value}%`
                    : `€${discount.discount_value}`}
                </Badge>
              )}
              <AvailabilityBadge article={article} />
            </div>
          </div>

          {article.availability_type === "preorder" && article.preorder_date && (
            <div className="text-sm mt-1 space-x-2">
              <span className="text-orange-600 font-medium">📅 Dal: {article.preorder_date}</span>
              <PreorderCountdown preorderDate={article.preorder_date} variant="inline" />
            </div>
          )}

          {article.availability_type === "reservation" && article.reservation_info && (
            <div className="text-sm text-blue-600 font-medium mt-1">ℹ️ {article.reservation_info}</div>
          )}

          <div className="text-xs text-gray-500">
            <div>
              Prezzo base: {formatPrice(article.price)} + IVA {article.iva_percentage}%
            </div>
            <div>SKU: {article.sku}</div>
          </div>
        </div>

        <div className="pt-3 border-t border-gray-800">
          <div className="flex gap-2">
            {isAvailable ? (
              <Button
                className="flex-1 bg-pink-600 hover:bg-pink-500 text-white font-bold"
                onClick={() => onAddToCart(article)}
                data-testid={`add-to-cart-${article.id}`}
              >
                🛒 Aggiungi al Carrello
              </Button>
            ) : (
              <Button className="flex-1 bg-gray-700 text-gray-400 cursor-not-allowed" disabled>
                Non Disponibile
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className={`px-3 ${
                isWishlisted
                  ? "bg-pink-500/10 border-pink-500/50 text-pink-400 hover:bg-pink-500/20"
                  : "bg-transparent border-gray-700 text-gray-400 hover:border-pink-500/50 hover:text-pink-400 hover:bg-transparent"
              }`}
              onClick={() => onToggleWishlist(article.id)}
              data-testid={`wishlist-toggle-${article.id}`}
              title={
                isWishlisted
                  ? "Rimuovi dai preferiti"
                  : isAvailable
                  ? "Aggiungi ai preferiti"
                  : "Aggiungi ai preferiti per essere avvisato"
              }
            >
              <Heart className={`w-4 h-4 ${isWishlisted ? "fill-current" : ""}`} />
            </Button>
          </div>
        </div>

        <ReviewSection
          articleId={article.id}
          reviews={reviews}
          showReviewForm={showReviewForm}
          reviewForm={reviewForm}
          setReviewForm={setReviewForm}
          onToggleForm={onToggleReviewForm}
          onSubmit={onSubmitReview}
        />
      </CardContent>
    </Card>
  );
};
